import { type ReactNode, type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'Modal';
import { LessonPlayerBot } from 'TutorBotAiTutorChat';
import { useAppHeaderViewModel } from 'NavigationHelpers';
import { useErrorLogService } from 'ErrorLogging';
import { useCurrentUser } from 'FrontRoyalAngular';
import clsx from 'clsx';
import { SupportModalBody } from './SupportModalBody';

export const SupportModalPresentation: FC<{
    onClose: () => void;
    AiTutorComponent: ReactNode | null;
    onlyAiTutor?: boolean;
}> = ({ onClose, AiTutorComponent, onlyAiTutor }) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'support.support' });

    const title = useMemo(() => (onlyAiTutor ? t('askAiTutor') : t('haveQuestions')), [t, onlyAiTutor]);

    return (
        <Modal title={title} size="2xl" onClose={onClose} dismissOnOverlayClick={false} dismissOnEscape={false}>
            <SupportModalBody AiTutorComponent={AiTutorComponent} onlyAiTutor={!!onlyAiTutor} />
        </Modal>
    );
};

export const SupportModal: FC<{ onClose: () => void; aiTutor: boolean; onlyAiTutor?: boolean }> = ({
    onClose,
    aiTutor,
    onlyAiTutor,
}) => {
    const currentUser = useCurrentUser();
    const AppHeaderViewModel = useAppHeaderViewModel();
    const ErrorLogService = useErrorLogService();

    const AiTutorComponent = useMemo(() => {
        if (!aiTutor) return null;
        const playerViewModel = AppHeaderViewModel.playerViewModel;
        playerViewModel?.launchBot(true);

        if (!currentUser || !playerViewModel?.botContentInfo) {
            ErrorLogService.notifyInProd('Cannot display LessonPlayerBot in SupportModal');
            return null;
        }

        return (
            <LessonPlayerBot
                currentUser={currentUser}
                contentInfo={playerViewModel.botContentInfo}
                uiContext={playerViewModel.botUiContext}
                initialMessage={playerViewModel.initialBotMessage}
                className={clsx(
                    'h-[500px] bg-gradient-to-tr from-[#F7F7F7] to-white p-0 sm:p-0',
                    onlyAiTutor && 'border-t border-slate-grey-200',
                )}
                supportTab
            />
        );
    }, [aiTutor, onlyAiTutor, ErrorLogService, currentUser, AppHeaderViewModel.playerViewModel]);

    return (
        <SupportModalPresentation onClose={onClose} AiTutorComponent={AiTutorComponent} onlyAiTutor={!!onlyAiTutor} />
    );
};
